import * as React from 'react';
import {
  AiOutlineCheckCircle,
  AiOutlineCloseCircle,
  AiOutlineInfoCircle,
  AiOutlineWarning
} from 'react-icons/ai';
import { IoClose } from 'react-icons/io5';
import { useTranslate } from '@tolgee/react';

type ToastVariant = 'success' | 'error' | 'warning' | 'info';

type ToastContextData = {
  add: (message: Omit<ToastMessage, 'id'> | string) => void;
};

const ToastContext = React.createContext({} as ToastContextData);

type ToastProviderProps = {
  children: React.ReactNode;
};

type ToastMessage = {
  id: string;
  title?: string;
  description: string;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  type?: ToastVariant;
};

const variantStyles: Record<ToastVariant, string> = {
  success: 'bg-green-50 border-green-500 text-green-800',
  error: 'bg-red-50 border-red-500 text-red-800',
  warning: 'bg-yellow-50 border-yellow-500 text-yellow-800',
  info: 'bg-blue-50 border-blue-500 text-blue-800'
};

const variantIcons: Record<ToastVariant, React.ReactNode> = {
  success: <AiOutlineCheckCircle size={20} className="text-green-500" />,
  error: <AiOutlineCloseCircle size={20} className="text-red-500" />,
  warning: <AiOutlineWarning size={20} className="text-yellow-500" />,
  info: <AiOutlineInfoCircle size={20} className="text-blue-500" />
};

function ToastItem({
  toast,
  onClose
}: {
  toast: ToastMessage;
  onClose: () => void;
}) {
  const { t } = useTranslate(['default']);
  const variant = toast.type || 'success';

  React.useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-lg border-l-4 shadow-lg ${variantStyles[variant]} animate-in slide-in-from-right`}
      role="alert">
      <span className="flex-shrink-0">
        {toast.icon || variantIcons[variant]}
      </span>
      <div className="flex-1">
        <p className="font-semibold">{toast.title || t(`toast.${variant}`)}</p>
        <p className="text-sm opacity-90">{toast.description}</p>
        {toast.children}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="flex-shrink-0 p-1 rounded hover:bg-black/10 border-none bg-transparent cursor-pointer"
        aria-label={t('common.close_toast')}>
        <IoClose size={18} />
      </button>
    </div>
  );
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = React.useState<ToastMessage[]>([]);

  const add = React.useCallback(
    (message: Omit<ToastMessage, 'id'> | string) => {
      const toast =
        typeof message === 'string' ? { description: message } : message;

      setToasts(state => [
        ...state,
        { id: crypto.randomUUID(), ...toast } as ToastMessage
      ]);
    },
    []
  );

  const removeToast = React.useCallback((toastId: string) => {
    setToasts(state => state.filter(item => item.id !== toastId));
  }, []);

  const providerValue = React.useMemo(() => ({ add }), [add]);

  return (
    <ToastContext.Provider value={providerValue}>
      {children}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm">
        {toasts.map(toast => (
          <ToastItem
            key={toast.id}
            toast={toast}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = React.useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
}
